import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import swal from 'sweetalert2'

import {
  faCheckCircle, faGrid, faList, faListCheck, faTimes, faTimesCircle,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AssignTemplatePagePermissionsModal from '@modals/AssignTemplatePagePermissionsModal/AssignTemplatePagePermissionsModal'
import {
  Box, Button, Columns, DashboardModule, Grid, HoverHintWrapper, LoadingModule, PageHeader,
  Tools,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useIntegration from '@hooks/useIntegration'

import type { BulkUpdateTemplatePages } from '@hooks/useTemplatePages'
import useTemplatePages from '@hooks/useTemplatePages'

import type { TemplatePageRequestOptions } from '@models/types'

import type { TemplatePageModel } from '@domain-group/realhub-backend-api-types'

import PageFilterModule from '@components/PageFilterModule'
import TemplatePageGridTile from '@components/TemplatePageGridTile/TemplatePageGridTile'

import EditTemplateModal from '@modals/EditTemplatePageModal/EditTemplatePageModal'

import CanvaTemplatePageBlankState from './CanvaTemplatePageBlankState'

import type { PageContextPayload } from '../List'
import ListView from './ListView'
import { AssignTemplatePagePermissionsModalPayload } from '@modals/AssignTemplatePagePermissionsModal/AssignTemplatePagePermissionsModal'
import BulkPermissionsModal from '@modals/BulkPermissionsModal/BulkPermissionsModal'
import Header from '../../../Header'

type BulkPermissionsModalPayload = { selectedTemplateIds: number[] }
const buildPageFilterFields = (isBrandUser: boolean) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        disabled: isBrandUser,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ brand_id }: { brand_id?: number }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'templateGroups',
        label: 'Template Group',
      },
      key: 'template_group_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brandThemes',
        label: 'Brand Theme',
      },
      key: 'brand_theme_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ brand_id }: { brand_id?: number }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
        label: 'Agency',
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'services',
        label: 'Service',
      },
      key: 'service_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'tags',
        label: 'Tag',
      },
      key: 'tag_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ service_id }: { service_id?: number }) => ({ service_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'serviceProducts',
        label: 'Product',
      },
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
        label: 'Publication',
      },
      key: 'publication_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
  assignTemplatePagePermissionsPayload: AssignTemplatePagePermissionsModalPayload | null,
  setAssignTemplatePagePermissionsPayload: (payload: AssignTemplatePagePermissionsModalPayload | null) => void
  showBulkPermissionsPayload: BulkPermissionsModalPayload,
  setShowBulkPermissionsPayload: (payload: BulkPermissionsModalPayload | null) => void
  selectedTemplateIds: number[],
  setSelectedTemplateIds: (ids: number[]) => void
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters, assignTemplatePagePermissionsPayload, setAssignTemplatePagePermissionsPayload, showBulkPermissionsPayload, setShowBulkPermissionsPayload, selectedTemplateIds, setSelectedTemplateIds } = props

  useLocation()

  const { currentUser, isBrandUser } = useCurrentUser()
  const scopedPageFilters = isBrandUser ? { ...pageFilters, brand_id: currentUser?.brand_id } : pageFilters
  const { integrated } = useIntegration()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden?.toString() ?? 'false'

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...scopedPageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: integrated,
    requestOptions: {
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
    },
  })

  const {
    callbacks: {
      bulkUpdateTemplatePages,
      loadMore,
      syncBrandTemplates,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const [selectedTemplatePage, setSelectedTemplatePage] = useState<null | TemplatePageModel>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')
  const [selectMode, setSelectMode] = useState(false)

  const toggleSelected = (id: number) => {
    setSelectedTemplateIds(
      selectedTemplateIds.includes(id)
        ? selectedTemplateIds.filter(selectedId => selectedId !== id)
        : [...selectedTemplateIds, id],
    )
  }

  const allVisibleSelected = !!filteredTemplatePages.length
    && filteredTemplatePages.every(templatePage => selectedTemplateIds.includes(templatePage.id))

  const selectAllVisible = () => {
    setSelectMode(true)
    setSelectedTemplateIds(
      Array.from(new Set([...selectedTemplateIds, ...filteredTemplatePages.map(templatePage => templatePage.id)])),
    )
  }

  const deselectAllVisible = () => {
    const visibleIds = new Set(filteredTemplatePages.map(templatePage => templatePage.id))
    setSelectedTemplateIds(selectedTemplateIds.filter(id => !visibleIds.has(id)))
  }

  const toggleSelectMode = () => {
    const next = !selectMode
    setSelectMode(next)

    if (!next) {
      setSelectedTemplateIds([])
    }
  }

  const archiveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => { setSelectedTemplateIds([]); setSelectMode(false) },
      buildActionParams: () => ([{
        templatePageIds: selectedTemplateIds,
        templatePageParams: {
          hidden: true,
        },
      }]),
    },
    toastText: 'Templates Archived',
  }

  const unarchiveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => { setSelectedTemplateIds([]); setSelectMode(false) },
      buildActionParams: () => ([{
        templatePageIds: selectedTemplateIds,
        templatePageParams: {
          hidden: false,
        },
      }]),
    },
    toastText: 'Templates Unarchived',
  }

  const confirmBulkTemplatePageAction = (params: {
    confirmButtonColor: string,
    confirmButtonText: string,
    payload: HandleCallbackActionParams,
    title: string,
  }) => {
    const {
      confirmButtonColor,
      confirmButtonText,
      payload,
      title,
    } = params

    swal
      .fire({
        title,
        text: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText,
        confirmButtonColor,
      })
      .then(({ value }) => {
        if (value) {
          handleCallbackAction(payload)
        }
      })
  }

  const updateFiltersAndClearSelectedIds: typeof updateFilters = (...args) => {
    setSelectedTemplateIds([])
    setSelectMode(false)

    return updateFilters(...args)
  }

  return (
    <>
      <Helmet>
        <title>Canva Templates | Template Manager</title>
      </Helmet>

      <Header activeTabBarItemKey='linked_templates'/>

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            { !loading && !hasFilteredTemplatePages && (
              <CanvaTemplatePageBlankState
                integrated={integrated}
                loading={loading}
                syncBrandTemplates={syncBrandTemplates}
              />
            )}

            {integrated && !loading && hasFilteredTemplatePages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Canva Templates">
                <Box flexDirection="column">
                  {viewMode === 'grid' && (
                    <Grid
                      // @ts-expect-error TS2322
                      gridGap="medium"
                      gridAutoRows="auto"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {filteredTemplatePages.map((templatePage: TemplatePageModel) => (
                        <TemplatePageGridTile
                          key={templatePage.id}
                          onToggleSelected={selectMode ? toggleSelected : undefined}
                          templatePage={templatePage}
                          setSelectedTemplatePage={setSelectedTemplatePage}
                          selectedId={selectedTemplateIds.includes(templatePage.id)}
                        />
                      ))}
                    </Grid>
                  )}

                  {viewMode === 'list' && (
                    <ListView
                      selectMode={selectMode}
                      setSelectedTemplatePage={setSelectedTemplatePage}
                      templatePages={filteredTemplatePages}
                      toggleSelected={toggleSelected}
                      selectedIds={selectedTemplateIds}
                    />
                  )}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          { integrated && (
          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={viewMode === 'list' ? faGrid : faList} />}
                onClick={viewMode === 'list' ? () => setViewMode('grid') : () => setViewMode('list')}
                size="medium"
              >
                {viewMode === 'list' ? 'View as Grid' : 'View as List'}
              </Button>

              {!!filteredTemplatePages.length && (
                <>
                  <Button
                    buttonStyle={selectMode ? 'primaryEdit' : 'secondaryUtility'}
                    marginTop="medium"
                    icon={<FontAwesomeIcon icon={selectMode ? faTimes : faListCheck} />}
                    onClick={() => { toggleSelectMode(); setSelectedTemplateIds([]) }}
                    size="medium"
                  >
                    Select Mode
                  </Button>

                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={allVisibleSelected ? faTimesCircle : faCheckCircle} />}
                    marginBottom="medium"
                    marginTop="medium"
                    onClick={() => (allVisibleSelected ? deselectAllVisible() : selectAllVisible())}
                    size="medium"
                  >
                    {allVisibleSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </>
              )}

              {!!selectedTemplateIds.length && selectMode && isHidden === 'false' && (
                <Button
                  buttonStyle="secondaryUtility"
                  marginBottom="medium"
                  onClick={() => {
                    confirmBulkTemplatePageAction({
                      confirmButtonColor: '#DD6B55',
                      confirmButtonText: 'Yes, archive them.',
                      // @ts-expect-error
                      payload: archiveSelectedTemplatePagesPayload,
                      title: 'Archive Selected Templates?',
                    })
                  }}
                  size="medium"
                >
                  Archive
                </Button>
              )}

              {!!selectedTemplateIds.length && selectMode && isHidden === 'true' && (
                <Button
                  buttonStyle="secondaryUtility"
                  marginBottom="medium"
                  onClick={() => {
                    confirmBulkTemplatePageAction({
                      confirmButtonColor: '#43ac6a',
                      confirmButtonText: 'Yes, unarchive them.',
                      // @ts-expect-error
                      payload: unarchiveSelectedTemplatePagesPayload,
                      title: 'Unarchive Selected Templates?',
                    })
                  }}
                  size="medium"
                >
                  Unarchive
                </Button>
              )}

              {selectedTemplateIds.length > 0 && (
                <HoverHintWrapper
                  boxProps={{ display: 'inline-block', position: 'relative', width: '100%' }}
                  hintComponent={(
                    <HoverHintWrapper.Hint>
                      Use the brand filters to select multiple
                      <br />
                      templates and assign permissions at once.
                    </HoverHintWrapper.Hint>
                  )}
                >
                  <Button
                    buttonStyle="secondaryUtility"
                    marginBottom="medium"
                    disabled={scopedPageFilters?.brand_id === undefined}
                    onClick={() => {
                      setShowBulkPermissionsPayload({ selectedTemplateIds })
                    }}
                    size="medium"
                  >
                    Assign Bulk Permissions
                  </Button>
                </HoverHintWrapper>
              )}
            </Tools>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters: updateFiltersAndClearSelectedIds }}
              defaultFilters={defaultFilters}
              externalFilters={isBrandUser ? { brand_id: currentUser?.brand_id } : undefined}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(isBrandUser)}
            />
          </Columns.Sidebar>
          )}

           {integrated && selectedTemplatePage !== null && (
            <EditTemplateModal
              showModal
              modalKey="EditTemplateModal"
              templatePage={selectedTemplatePage}
              callbacks={{
                closeModal: () => { setSelectedTemplatePage(null) },
                editTemplatePage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              }}
            />
          )}
          {assignTemplatePagePermissionsPayload !== null && (
          <AssignTemplatePagePermissionsModal
            callbacks={{
              bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              closeModal: () => setAssignTemplatePagePermissionsPayload(null),
            }}
            showModal={true}
            payload={assignTemplatePagePermissionsPayload}
          />
        )}
          {showBulkPermissionsPayload !== null && (
          <BulkPermissionsModal
            modalKey="BulkPermissionsModal"
            selectedTemplateIds={selectedTemplateIds}
            callbacks={{
              closeModal: () => {
                setShowBulkPermissionsPayload(null)
                setSelectedTemplateIds([])
              },
              assignManually: ({ additionalTemplatePageIds, stepKey, templatePageId }) => {
                setShowBulkPermissionsPayload(null)
                setSelectedTemplateIds([])
                setAssignTemplatePagePermissionsPayload({
                  additionalTemplatePageIds,
                  stepKey,
                  templatePage: { id: templatePageId },
                })
              },
            }}
            showModal
          />
        )}
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
